// src/hooks/useNotifications.js
import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'react-toastify'
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, deleteNotification, getNotificationIcon } from '../services/notificationService'

/**
 * Custom hook for managing notifications
 * @param {Object} options - Hook options
 * @param {boolean} options.autoFetch - Auto-fetch notifications on mount (default: true)
 * @param {number} options.pollInterval - Polling interval in ms (default: 30000, set 0 to disable)
 * @param {boolean} options.showToasts - Show toast for new notifications (default: true)
 * @returns {Object} - Notification state and actions
 */
const useNotifications = (options = {}) => {
  const { autoFetch = true, pollInterval = 30000, showToasts = true } = options

  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    hasNext: false,
    hasPrev: false,
    total: 0
  })

  const pollIntervalRef = useRef(null)
  const previousUnreadCountRef = useRef(0)

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async (page = 1, filters = {}) => {
    setLoading(true)
    setError(null)

    try {
      const response = await getNotifications({ page, ...filters })

      setNotifications(response.results || [])
      setPagination({
        page,
        hasNext: !!response.next,
        hasPrev: !!response.previous,
        total: response.count || 0
      })

      return response
    } catch (err) {
      setError(err.message || 'Failed to fetch notifications')
      console.error('Error fetching notifications:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Fetch unread count from API
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await getUnreadCount()
      const newCount = response.unread_count || 0

      // Show toast if new notifications arrived
      if (showToasts && newCount > previousUnreadCountRef.current && previousUnreadCountRef.current > 0) {
        const diff = newCount - previousUnreadCountRef.current
        toast.info(`You have ${diff} new notification${diff > 1 ? 's' : ''}`, {
          toastId: 'new-notifications',
          icon: '🔔'
        })
      }

      previousUnreadCountRef.current = newCount
      setUnreadCount(newCount)

      return newCount
    } catch (err) {
      console.error('Error fetching unread count:', err)
      return null
    }
  }, [showToasts])

  /**
   * Mark a single notification as read
   */
  const handleMarkAsRead = useCallback(async notificationId => {
    try {
      await markAsRead(notificationId)

      // Update local state
      setNotifications(prev => prev.map(n => (n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)))

      setUnreadCount(prev => Math.max(0, prev - 1))

      return true
    } catch (err) {
      console.error('Error marking notification as read:', err)
      toast.error('Failed to mark notification as read')
      return false
    }
  }, [])

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead()

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({
          ...n,
          is_read: true,
          read_at: n.read_at || new Date().toISOString()
        }))
      )

      setUnreadCount(0)
      previousUnreadCountRef.current = 0

      toast.success('All notifications marked as read')
      return true
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
      toast.error('Failed to mark all notifications as read')
      return false
    }
  }, [])

  /**
   * Delete a notification
   */
  const handleDeleteNotification = useCallback(
    async notificationId => {
      try {
        await deleteNotification(notificationId)

        // Update local state
        const notification = notifications.find(n => n.id === notificationId)
        setNotifications(prev => prev.filter(n => n.id !== notificationId))

        if (notification && !notification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1))
        }

        setPagination(prev => ({
          ...prev,
          total: Math.max(0, prev.total - 1)
        }))

        return true
      } catch (err) {
        console.error('Error deleting notification:', err)
        toast.error('Failed to delete notification')
        return false
      }
    },
    [notifications]
  )

  /**
   * Refresh notifications (fetch first page and update unread count)
   */
  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(1), fetchUnreadCount()])
  }, [fetchNotifications, fetchUnreadCount])

  /**
   * Load next page of notifications
   */
  const loadNextPage = useCallback(async () => {
    if (pagination.hasNext) {
      return fetchNotifications(pagination.page + 1)
    }
    return null
  }, [fetchNotifications, pagination])

  /**
   * Load previous page of notifications
   */
  const loadPrevPage = useCallback(async () => {
    if (pagination.hasPrev) {
      return fetchNotifications(pagination.page - 1)
    }
    return null
  }, [fetchNotifications, pagination])

  /**
   * Add a new notification to the list (for real-time updates)
   */
  const addNotification = useCallback((notification, showToast = true) => {
    setNotifications(prev => [notification, ...prev])
    setUnreadCount(prev => prev + 1)
    previousUnreadCountRef.current += 1
    setPagination(prev => ({
      ...prev,
      total: prev.total + 1
    }))

    if (showToast) {
      toast.info(notification.title || notification.message, {
        toastId: notification.id,
        icon: () => <i className={`ph ${getNotificationIcon(notification.notification_type)}`} />
      })
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (autoFetch && token) {
      fetchNotifications(1)
      fetchUnreadCount()
    }
  }, [autoFetch, fetchNotifications, fetchUnreadCount])

  // Polling for new notifications
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (pollInterval > 0 && token) {
      pollIntervalRef.current = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
        fetchUnreadCount()
      }, pollInterval)
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [pollInterval, fetchUnreadCount])

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    pagination,

    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDeleteNotification,
    refresh,
    loadNextPage,
    loadPrevPage,
    addNotification
  }
}

export default useNotifications
